import { useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import type { document } from '@/lib/client';
import { cn } from '@/lib/utils';
import { useTraceStore } from '@/stores/traceStore';
import { progressOf, ZOOM_FACTS } from '@/lib/zacking';
import { ZacMascot } from './ZacMascot';

/** What ZAC does while a request is read and planned, in the order it does it. */
const STAGES: Array<{ status: document.DocumentStatus; label: string; expectedMs: number }> = [
  { status: 'decomposing', label: 'Reading the request', expectedMs: 60_000 },
  { status: 'planning', label: 'Matching it to Zoom and reading your account', expectedMs: 120_000 },
];

const FACT_MS = 7_000;

/**
 * While ZAC is zacking (a request read and planned): the stages with a bar for the one under way, ZAC's latest line
 * about it from the reasoning trace, and a fact about Zoom every few seconds.
 */
export function Zacking({ doc, compact = false }: { doc: Pick<document.DocumentView, 'id' | 'status' | 'updated_at' | 'round' | 'max_rounds'>; compact?: boolean }) {
  const [now, setNow] = useState(() => Date.now());
  const [fact, setFact] = useState(() => Math.floor(Math.random() * ZOOM_FACTS.length));
  const read = useTraceStore((s) => s.read);
  const line = useTraceStore((s) => s.active.find((a) => a.id === doc.id)?.line ?? null);

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1_000);
    const facts = window.setInterval(() => setFact((f) => (f + 1) % ZOOM_FACTS.length), FACT_MS);
    const trace = window.setInterval(() => { void read(); }, 2_500);
    void read();
    return () => { window.clearInterval(tick); window.clearInterval(facts); window.clearInterval(trace); };
  }, [read]);

  const at = STAGES.findIndex((s) => s.status === doc.status);
  const stage = STAGES[at];
  const progress = stage === undefined ? 0 : progressOf(now - new Date(doc.updated_at).getTime(), stage.expectedMs);

  return (
    <div className={cn('rounded-lg border border-border bg-card', compact ? 'px-3 py-2.5' : 'px-4 py-4')}>
      <div className="flex items-center gap-4">
        <ZacMascot className={cn('shrink-0 text-foreground', compact ? 'h-14 w-20' : 'h-24 w-32')} />
        <div className="min-w-0 flex-1">
          <ol className="flex flex-col gap-2">
            {STAGES.map((s, i) => (
              <li key={s.status} className="space-y-1">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className={cn('flex items-center gap-1.5', i === at ? 'font-medium text-foreground' : i < at ? 'text-emerald-400' : 'text-muted-foreground')}>
                    {i === at ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span className="w-3.5 text-center">{i < at ? '✓' : '·'}</span>}
                    {s.label}{s.status === 'decomposing' && i === at && doc.round > 0 ? ` · round ${doc.round} of ${doc.max_rounds}` : ''}
                  </span>
                  {i === at && <span className="tabular-nums text-muted-foreground">{progress}%</span>}
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                  <div className={cn('h-full rounded-full transition-[width] duration-1000 ease-out', i < at ? 'bg-emerald-500/70' : 'bg-primary')} style={{ width: `${i < at ? 100 : i === at ? progress : 0}%` }} />
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
      {line !== null && <p className="mt-3 truncate text-xs text-muted-foreground" title={line}>Now: {line}</p>}
      {!compact && (
        <p key={fact} className="mt-3 flex items-start gap-2 text-xs text-muted-foreground animate-in fade-in duration-500">
          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
          <span><span className="font-medium text-foreground">While ZAC is zacking:</span> {ZOOM_FACTS[fact]}</span>
        </p>
      )}
    </div>
  );
}
